// §13 — notifications.
//
// "`post()` returns an id. A notification badges its app's bar icon, raises the skin's
// `NotificationHost` toast for 6 s (or until dismissed), and lands in the Notification Center.
// Actions declared by the app are rendered as buttons and reported back over `notify/action`.
// **Clicking the body focuses the window that sent it.** Center holds 100 entries, grouped by app,
// "Clear all" per group. Do-not-disturb is a Settings toggle that suppresses toasts but not the
// Center."
//
// Two things are worth naming because they are what the service is *for*:
//
//   • **A toast and a Center entry are not the same object's two states.** Do-not-disturb takes the
//     first and leaves the second, and 6 s takes the first and leaves the second, so the toast is a
//     membership with a clock and the entry is the record. One list would have made DND either
//     lose the notification or show it anyway.
//   • **The sender is a *window*, not an app.** §13's click-to-focus has to reach the instance that
//     posted, and an app with three windows open has three answers to "which one said this".
import type { NotificationInput } from '@hangar/shared';
import type { Bus } from '../bus';

/** §13 — "raises the skin's `NotificationHost` toast for 6 s (or until dismissed)". */
export const TOAST_MS = 6_000;

/** §13 — "Center holds 100 entries". */
export const CENTER_LIMIT = 100;

export interface Notification {
  id: string;
  appId: string;
  /** The window that posted it. §13: clicking the body focuses *that* window, not the app. */
  instanceKey: string;
  title: string;
  body?: string;
  /**
   * §4.1 — `actions?: string[]`. The string is both the button's label and the id reported back,
   * which is what makes an action declared by an app answerable without a second table (D-138).
   */
  actions: readonly string[];
  at: number;
}

/** §13 — the Center, "grouped by app", with a "Clear all" per group. */
export interface NotificationGroup {
  appId: string;
  /** Newest first, like the Center itself. */
  items: readonly Notification[];
}

/** Who posted. Both halves matter: the app for the badge, the window for the click. */
export interface NotifySource {
  appId: string;
  instanceKey: string;
}

export interface NotifyService {
  /** §13 — returns the id. The Center always takes it; whether a toast rises is DND's answer. */
  post(input: NotificationInput, from: NotifySource, at: number): string;
  /** §13 — the Center, newest first, capped at `CENTER_LIMIT`. */
  center(): readonly Notification[];
  /** §13 — the same entries grouped by app, most-recently-posted group first. */
  groups(): readonly NotificationGroup[];
  /** The toasts currently up. Empty under do-not-disturb (§13). */
  toasts(): readonly Notification[];
  /**
   * §13 — "badges its app's bar icon": how many of this app's notifications are in the Center.
   *
   * §13 describes no read state, so the badge is the Center's own count and "Clear all" is what
   * clears it — the one gesture §13 does give for a group (D-139).
   */
  badge(appId: string): number;
  /** §13 — "or until dismissed". The Center entry is untouched. */
  dismissToast(id: string): void;
  /** Drop every toast whose 6 s is up. The host runs the clock; the rule lives here. */
  sweep(at: number): void;
  /** When the earliest toast expires, or null when none is up — one timer instead of a poll. */
  nextExpiry(): number | null;
  /** §13 — clicking the body focuses the window that sent it, and takes the toast down. */
  activate(id: string): void;
  /** §13 — an action button. Reported back over `notify/action`; unknown actions are refused. */
  act(id: string, action: string): void;
  /** §13 — "Clear all" per group. `null` clears the whole Center. */
  clear(appId: string | null): void;
  subscribe(onChange: () => void): () => void;
}

export interface NotifyOptions {
  bus: Bus;
  /** §13 / §14 — the Settings toggle, read at each post rather than remembered. */
  doNotDisturb: () => boolean;
  /**
   * §13 — click-to-focus, with the notification's id (D-345).
   *
   * The id is here because "focus the window that sent it" is not always the right answer: the
   * shell posts on behalf of features whose window may not exist — a chat message arriving with
   * Chat shut is the case D-338 exists for — and those need to *open* something rather than focus
   * a window nobody has. The `instanceKey` still decides for every ordinary app notification.
   */
  focusWindow: (instanceKey: string, id: string) => void;
  /**
   * §13 — "reported back over `notify/action`". Whichever transport the sender lives behind: the
   * kernel owns that choice for the same reason it owns `fireCommand` and `deliverPayload`.
   */
  deliverAction: (instanceKey: string, id: string, action: string) => boolean;
  /** §15 — the `notify` sound. Suppressed with the toast under DND: a silent toast is the point. */
  play?: () => void;
  newId?: () => string;
}

export function createNotifyService(options: NotifyOptions): NotifyService {
  let counter = 0;
  const newId = options.newId ?? (() => `n${(counter += 1)}`);

  /** Newest first. Trimmed to `CENTER_LIMIT` on every post. */
  const entries: Notification[] = [];
  /** Notification id → when its toast is due to come down. */
  const toastUntil = new Map<string, number>();
  const listeners = new Set<() => void>();

  const announce = (): void => {
    for (const listener of [...listeners]) listener();
  };

  const find = (id: string): Notification | undefined => entries.find((entry) => entry.id === id);

  /** A local function rather than a method, so it works when the service is destructured. */
  const dismissToast = (id: string): void => {
    if (!toastUntil.delete(id)) return;
    options.bus.emit('notify:dismissed', { id });
    announce();
  };

  return {
    post(input, from, at) {
      const id = newId();
      const notification: Notification = {
        id,
        appId: from.appId,
        instanceKey: from.instanceKey,
        title: input.title,
        ...(input.body !== undefined ? { body: input.body } : {}),
        actions: [...(input.actions ?? [])],
        at,
      };
      entries.unshift(notification);
      // §13 — 100 entries. The oldest leaves, and its toast with it: a toast for an entry that is
      // no longer in the Center could not be clicked through to anything.
      while (entries.length > CENTER_LIMIT) {
        const dropped = entries.pop();
        if (dropped) toastUntil.delete(dropped.id);
      }

      // §13 — do-not-disturb "suppresses toasts but not the Center". The entry above is already
      // recorded; only the toast and its sound are conditional.
      if (!options.doNotDisturb()) {
        toastUntil.set(id, at + TOAST_MS);
        options.play?.();
      }

      options.bus.emit('notify:posted', { id, appId: from.appId });
      announce();
      return id;
    },

    center() {
      return entries;
    },

    groups() {
      const byApp = new Map<string, Notification[]>();
      // `entries` is newest first, so both the group order and each group's order fall out of one
      // pass — the first time an app appears is its most recent notification.
      for (const entry of entries) {
        const items = byApp.get(entry.appId);
        if (items) items.push(entry);
        else byApp.set(entry.appId, [entry]);
      }
      return [...byApp].map(([appId, items]) => ({ appId, items }));
    },

    toasts() {
      return entries.filter((entry) => toastUntil.has(entry.id));
    },

    badge(appId) {
      return entries.reduce((count, entry) => (entry.appId === appId ? count + 1 : count), 0);
    },

    dismissToast,

    sweep(at) {
      let changed = false;
      for (const [id, until] of [...toastUntil]) {
        if (until > at) continue;
        toastUntil.delete(id);
        options.bus.emit('notify:dismissed', { id });
        changed = true;
      }
      if (changed) announce();
    },

    nextExpiry() {
      let earliest: number | null = null;
      for (const until of toastUntil.values()) {
        if (earliest === null || until < earliest) earliest = until;
      }
      return earliest;
    },

    activate(id) {
      const entry = find(id);
      if (!entry) return;
      // The toast goes first: focusing is what the user asked for, and a toast still sitting over
      // the window they just asked to see would be the shell arguing with them.
      dismissToast(id);
      options.focusWindow(entry.instanceKey, id);
    },

    act(id, action) {
      const entry = find(id);
      // An action the app did not declare is not an action. The buttons are the shell's, so this
      // can only be reached out of band — and out of band is exactly when it matters.
      if (!entry || !entry.actions.includes(action)) return;
      dismissToast(id);
      options.deliverAction(entry.instanceKey, id, action);
      options.bus.emit('notify:action', { id, action });
    },

    clear(appId) {
      const kept = appId === null ? [] : entries.filter((entry) => entry.appId !== appId);
      if (kept.length === entries.length) return;
      for (const entry of entries) {
        if (!kept.includes(entry)) toastUntil.delete(entry.id);
      }
      entries.length = 0;
      entries.push(...kept);
      options.bus.emit('notify:cleared', { appId });
      announce();
    },

    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
  };
}
